import React, { useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/components";
import { Button } from "@/shared/components";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/shared/components";
import { Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Repository } from '@/shared/services/supabaseService';

interface SingleRepoSelectorProps {
  repositories: Repository[];
  selectedRepoId?: string;
  onSelectRepo: (repoId?: string) => void;
  className?: string;
}

const SingleRepoSelector: React.FC<SingleRepoSelectorProps> = ({
  repositories,
  selectedRepoId,
  onSelectRepo,
  className,
}) => {
  const [open, setOpen] = useState(false);

  const selectedRepo = repositories.find(repo => repo.id.toString() === selectedRepoId);

  return (
    <div className={cn("w-[300px]", className)}>
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    className="w-full justify-between rounded-none font-mono text-xs h-10 border-neutral-950 dark:border-neutral-100 bg-background"
                >
                    <span className="truncate uppercase font-bold">
                        {selectedRepo ? selectedRepo.fullName : "[ ALL_REPOSITORIES ]"}
                    </span>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[300px] p-0 rounded-none border-neutral-950 dark:border-neutral-100" align="start">
                <Command className="rounded-none">
                    <CommandInput placeholder="SEARCH_REPOS..." className="font-mono text-xs rounded-none" />
                    <CommandList>
                        <CommandEmpty className="py-4 text-center text-xs font-mono text-muted-foreground">NO_REPO_FOUND</CommandEmpty>
                        <CommandGroup>
                            <CommandItem
                                onSelect={() => {
                                    onSelectRepo(undefined);
                                    setOpen(false);
                                }}
                                className="font-mono text-xs cursor-pointer hover:bg-neutral-100 dark:hover:bg-neutral-900 aria-selected:bg-neutral-100 dark:aria-selected:bg-neutral-900 rounded-none py-3"
                            >
                                <Check
                                    className={cn(
                                        "mr-2 h-4 w-4",
                                        !selectedRepoId ? "opacity-100" : "opacity-0"
                                    )}
                                />
                                [ ALL_REPOSITORIES ]
                            </CommandItem>
                            {repositories.map((repo) => (
                                <CommandItem
                                    key={repo.id}
                                    value={repo.fullName}
                                    onSelect={() => {
                                        onSelectRepo(repo.id.toString());
                                        setOpen(false);
                                    }}
                                    className="font-mono text-xs cursor-pointer hover:bg-neutral-100 dark:hover:bg-neutral-900 aria-selected:bg-neutral-100 dark:aria-selected:bg-neutral-900 rounded-none py-3"
                                >
                                    <Check
                                        className={cn(
                                            "mr-2 h-4 w-4",
                                            selectedRepoId === repo.id.toString() ? "opacity-100" : "opacity-0"
                                        )}
                                    />
                                    <div className="flex flex-col truncate">
                                        <span className="font-bold truncate">{repo.name}</span>
                                        <span className="text-[10px] text-muted-foreground truncate">{repo.fullName}</span>
                                    </div>
                                </CommandItem>
                            ))}
                        </CommandGroup>
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    </div>
  );
};

export default SingleRepoSelector;
